import type { MeetingDTO, MeetingTranscriptEntry } from "../../shared/types";

/** Pandas is on its way to the call or in it. */
export const IN_CALL: MeetingDTO["status"][] = ["requested", "joining", "waiting", "in_call"];

/** The transcript the minutes were written from: the recording's merged with the captions and chat, else the captions alone, in time order. */
export function transcriptOf(m: Pick<MeetingDTO, "audioTranscript" | "transcript">): MeetingTranscriptEntry[] {
  return [...(m.audioTranscript.length ? m.audioTranscript : m.transcript)].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

/** When the meeting starts on its own timeline: the bot joining, or the first thing it caught if that came earlier. */
export function meetingStart(m: Pick<MeetingDTO, "joinedAt" | "createdAt" | "frames" | "audioChunks">, transcript: MeetingTranscriptEntry[]): number {
  const times = [m.joinedAt ?? m.createdAt, transcript[0]?.at, m.frames[0]?.at, m.audioChunks[0]?.startedAt].map((t) => (t ? Date.parse(t) : NaN)).filter(Number.isFinite);
  return times.length ? Math.min(...times) : Date.parse(m.createdAt);
}

/** 75 → "1:15", 3725 → "1:02:05": a moment into the meeting. */
export function sinceStart(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const pad = (n: number) => String(n).padStart(2, "0");
  const h = Math.floor(s / 3600);
  return h ? `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` : `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

/** "45 min", "1h", "1h 30m"; null while it has no end. */
export function meetingLength(from: string | null, to: string | null): string | null {
  if (!from || !to) return null;
  const minutes = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  return minutes % 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes / 60}h`;
}

/** A line of the transcript said in the meeting's chat: Meet marks its sender "Name (chat)". */
export const isChat = (speaker: string) => / \(chat\)$/.test(speaker);
export const speakerName = (speaker: string) => speaker.replace(/ \(chat\)$/, "");

/** Words of a name, as people write it: no markdown, no accents, no case. */
const words = (text: string) =>
  text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/**
 * Whether a table's Owner cell names this person, by name — good enough for display (Dimi, 2026-11-03): its first word
 * is one of theirs (or its first four letters or more begin one), and every other word begins one of theirs.
 */
export function namesPerson(owner: string, name: string): boolean {
  const said = words(owner);
  const theirs = words(name);
  if (!said.length || !theirs.length) return false;
  const [first, ...rest] = said;
  const opens = (w: string) => theirs.some((t) => t === w || (w.length >= 4 && t.startsWith(w)));
  return opens(first!) && rest.every((w) => theirs.some((t) => t.startsWith(w)));
}

const isRow = (line: string | undefined) => Boolean(line && /^\s*\|.*\|\s*$/.test(line));
const isRule = (line: string | undefined) => Boolean(line && /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line));
const firstCell = (line: string) => line.trim().replace(/^\|/, "").split("|")[0] ?? "";

/**
 * This person's own action items: the rows of the minutes' tables whose first column, the owner, names them, under
 * the table's own heading row, links as written. The minutes are written in the meeting's language, so the table is
 * found by its shape (`| Owner | Action | Due |`), never by its heading. Null when there are none.
 */
export function myActionItems(minutes: string | null, name: string): string | null {
  if (!minutes) return null;
  const lines = minutes.split("\n");
  const found: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (!isRow(lines[i]) || !isRule(lines[i + 1])) continue;
    let end = i + 2;
    while (end < lines.length && isRow(lines[end])) end++;
    const mine = lines.slice(i + 2, end).filter((row) => namesPerson(firstCell(row), name));
    if (mine.length) found.push([lines[i], lines[i + 1], ...mine].join("\n"));
    i = end - 1;
  }
  return found.length ? found.join("\n\n") : null;
}

/** Why the bot left (AIT-364), in two or three words for a table; the row's `endDetail` says it in a sentence. */
export const END_WORDS: Record<NonNullable<MeetingDTO["endReason"]>, string> = {
  alone: "nobody came", everyone_left: "everyone left", removed: "removed", ended_by_host: "ended by the host", call_ended: "the call ended",
  not_admitted: "not let in", join_failed: "couldn't join", failed: "failed in the call", hung: "stopped responding",
  asked_to_leave: "asked to leave", time_limit: "time limit", lost_contact: "lost contact", no_slot: "no free slot",
};

/** "saw 3 at most, the first at 10:03", "saw nobody", or null when the call never said how many (AIT-364). */
export function peopleWords(m: Pick<MeetingDTO, "peopleSeen">): string | null {
  const { max, firstAt } = m.peopleSeen;
  if (max === null) return null;
  if (max === 0) return "saw nobody";
  const at = firstAt ? new Date(firstAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) : null;
  return `saw ${max} at most${at ? `, the first at ${at}` : ""}`;
}
